import { Fragment, lazy, RefObject, Suspense } from 'react'
import Container from 'openspace/components/Container'
import { HomeViewProps } from './Home.types'
import Styles from './Home.module.scss'
import Text from '@onepercentio/one-ui/components/Text'
import { tE, tO } from 'translate/i18n'
import { useGalleries } from 'core/logic/gallery/gallery.hook'
import Section from 'openspace/components/Section'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider'
import LazyBadge from 'openspace/components/LazyBadge'
import Footer from 'components/Footer'
import { HomeTestIds } from './Home.e2e'
import GalleryCard from 'openspace/components/GalleryCard'
import { OfferType } from 'core/logic/drop/drop.types'
import { Paginable } from '@onepercentio/one-ui/hooks/usePagination'
import DropItem from 'openspace/components/DropItem'
import { DropItemViewMode } from 'openspace/components/DropItem/DropItem.types'
import { IPOBanner } from 'pages/Unauthenticated/Drops/Details/Models/IPO/IPO.logic'
import { useFeatures } from 'context/Feature'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { default as ChallengeSection } from 'features/Challenge/ChallengeSection'
import GalleryCardVariables from 'openspace/components/GalleryCard/GalleryCard.module.scss'
import HeroSection from 'openspace/components/HeroSection'
import ActiveOffersSection from 'openspace/components/ActiveOffersSection'
import IssuersSection from 'openspace/components/IssuersSection'
import { OfferCardProps } from 'openspace/components/OfferCard'
import { IssuerCardProps } from 'openspace/components/IssuerCard'

const LogoType = lazy(() => import('../../../components/Background/BG'))

export function GalleriesSection({ onSeeAll }: { onSeeAll: () => void }) {
  const { galleries } = useGalleries()
  const galleriesBaseWidth =
    galleries && galleries.length > 0 && galleries[0].type === 'cvm88'
      ? Number(GalleryCardVariables.baseWidthcvm88) + 16
      : Number(GalleryCardVariables.baseWidthdefault) + 26
  return (
    <>
      <Section
        data-testid={HomeTestIds.GallerySection}
        title={tE('home.sections.gallery.title')}
        seeAll={{
          onClick: onSeeAll,
          label: tO('home.sections.gallery.action.seeAll'),
        }}
        src={galleries || []}
        jsx={{
          empty: null,
          loading: null,
          baseWidth: galleriesBaseWidth,
          factory: (b) => (
            <GalleryCard
              data-testid={HomeTestIds.gallery}
              gallery={b}
              shouldExpandOnMount={false}
              mode={b.highlight ? 'highlight' : 'default'}
            />
          ),
        }}
      />
    </>
  )
}

export function LatestItensSection({
  drops,
  containerRef,
}: {
  drops: Paginable<OfferType>
  containerRef: RefObject<HTMLDivElement>
}) {
  return (
    <Section
      ref={containerRef}
      data-testid={HomeTestIds.ItemsSection}
      title={tO('home.sections.latest.title')}
      src={drops}
      jsx={{
        empty: null,
        loading: null,
        baseWidth: 302 + 26,
        factory: (asset) => (
          <DropItem
            mode={DropItemViewMode.EXPANDED}
            dropOrAsset={asset}
            key={asset.id}
            availability={'open'}
            data-testid={HomeTestIds.asset}
          />
        ),
      }}
    />
  )
}

export function Partnership() {
  return (
    <Container className={Styles.partnership}>
      <div>
        <Suspense fallback={<svg />}>
          <LogoType />
        </Suspense>
        <LazyBadge className={Styles.badge} />
      </div>
      <div>
        <Text type={FigmaTypo.H2}>{tO('home.sections.partnership.title')}</Text>
        <Text type={FigmaTypo.P16}>
          {tO('home.sections.partnership.description')}
        </Text>
      </div>
    </Container>
  )
}

export function HighlightsSection({
  drops,
}: {
  drops: Paginable<OfferType>
}) {
  return (
    <Section
      data-testid={HomeTestIds.ItemsSection}
      title={tO('home.sections.highlights.title')}
      src={drops}
      jsx={{
        empty: null,
        loading: null,
        baseWidth: 302 + 26,
        factory: (asset) => {
          switch (asset?.type) {
            case 'cvm88':
              return <IPOBanner offer={asset} />
            default:
              throw new Error(
                `There is no highlight banner defined for offers of type ${asset?.type || 'undefined'}`
              )
          }
        },
      }}
    />
  )
}

export { ChallengeSection }

/**
 * Where the user checks out the galeries
 **/
export default function HomeView(props: HomeViewProps) {
  const { hasGalleries } = useFeatures()

  const mockOffers: OfferCardProps[] = props.dropsPaginable.results.slice(0, 5).map((drop) => ({
    title: drop.name || 'Sem título',
    category: drop.type === 'cvm88' ? 'renda fixa' : 'outros',
    percentageSold: 75,
    monthlyRate: '1.5% a.m.',
    totalSold: 'R$ 150.000',
    availability: 'R$ 50.000',
    investors: 42,
    maturityDate: '12/2025',
    daysRemaining: '45 dias restantes',
    onCtaClick: () => console.log('Invest clicked'),
  }))

  const mockIssuers: IssuerCardProps[] = [
    {
      name: 'Emissor A',
      description: 'Descrição do emissor',
      totalVolume: 'R$ 2.5M',
      projectCount: 4,
    },
    {
      name: 'Emissor B',
      description: 'Descrição do emissor',
      totalVolume: 'R$ 1.8M',
      projectCount: 3,
    },
  ]

  return (
    <>
      <HeroSection />
      <ActiveOffersSection offers={mockOffers} />
      <IssuersSection issuers={mockIssuers} />
      <Footer />
    </>
  )
}
